import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchChargesRequest,
  fetchChargesSuccess,
  fetchChargesFailure,
  searchChargesRequest,
  searchChargesSuccess,
  searchChargesFailure,
  fetchChargeDetailsRequest,
  fetchChargeDetailsSuccess,
  fetchChargeDetailsFailure,
  createChargeRequest,
  createChargeSuccess,
  createChargeFailure,
  updateChargeRequest,
  updateChargeSuccess,
  updateChargeFailure,
  deleteChargeRequest,
  deleteChargeSuccess,
  deleteChargeFailure,
} from '../slices/charge.slice';

import {
  getCharges,
  getChargeById,
  createCharge,
  updateCharge,
  deleteCharge,
  searchCharges,
} from '@/services/chargeService';

import { 
  CreateChargeDto,
  UpdateChargeDto,
  FetchChargesOptions 
} from '@/types/models/charge';
import { toApiError } from '@/lib/errorHelpers';

// Fetch charges saga
function* fetchChargesSaga(action: PayloadAction<FetchChargesOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(getCharges, action.payload);
    yield put(fetchChargesSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: action.payload.page || 1,
        total: response.length || 0,
        pageSize: action.payload.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (action.payload.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(fetchChargesFailure(toApiError(error)));
  }
}

// Search charges saga
function* searchChargesSaga(action: PayloadAction<{ query: string; options?: FetchChargesOptions }>): Generator<any, void, any> {
  try {
    const { query, options = {} } = action.payload;
    const response: any = yield call(searchCharges, query, options);
    yield put(searchChargesSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: options.page || 1,
        total: response.length || 0,
        pageSize: options.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (options.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(searchChargesFailure(toApiError(error)));
  }
}

// Fetch charge details saga
function* fetchChargeDetailsSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    const response: any = yield call(getChargeById, action.payload.id);
    yield put(fetchChargeDetailsSuccess(response));
  } catch (error: any) {
    yield put(fetchChargeDetailsFailure(toApiError(error)));
  }
}

// Create charge saga
function* createChargeSaga(action: PayloadAction<CreateChargeDto>): Generator<any, void, any> {
  try {
    const response: any = yield call(createCharge, action.payload);
    yield put(createChargeSuccess(response));
  } catch (error: any) {
    yield put(createChargeFailure(toApiError(error)));
  }
}

// Update charge saga
function* updateChargeSaga(action: PayloadAction<{ id: string; data: UpdateChargeDto }>): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
    const response: any = yield call(updateCharge, id, data);
    yield put(updateChargeSuccess(response));
  } catch (error: any) {
    yield put(updateChargeFailure(toApiError(error)));
  }
}

// Delete charge saga
function* deleteChargeSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    yield call(deleteCharge, action.payload.id);
    yield put(deleteChargeSuccess({ id: action.payload.id }));
  } catch (error: any) {
    yield put(deleteChargeFailure(toApiError(error)));
  }
}

// Root charge saga
export function* chargeSaga(): Generator<any, void, any> {
  yield takeLatest(fetchChargesRequest.type, fetchChargesSaga);
  yield takeLatest(searchChargesRequest.type, searchChargesSaga);
  yield takeLatest(fetchChargeDetailsRequest.type, fetchChargeDetailsSaga);
  yield takeLatest(createChargeRequest.type, createChargeSaga);
  yield takeLatest(updateChargeRequest.type, updateChargeSaga);
  yield takeLatest(deleteChargeRequest.type, deleteChargeSaga);
}

export default chargeSaga;